/**
 * Componente ReturnedAgGridTable
 * 
 * Wrapper sobre AgGridTable para la lista de Recepciones (Returned).
 * Maneja la configuración específica de columnas, sorting y acciones.
 * 
 * Diferencias con Orders:
 * - NO tiene selección múltiple (enableSelection=false)
 * - NO tiene SelectionToolbar
 * - NO tiene modales de acciones masivas
 * - Acciones simples por fila (Ver/Ubicar/Recepcionar)
 */

import React, { useCallback, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { ReturnedModel } from '../../models/Returned';

interface ReturnedAgGridTableProps {
  returned: ReturnedModel[];
  isLoading?: boolean;
  isFetching?: boolean; // 🎯 Añadir isFetching para control de opacidad
  className?: string;
  selectedColumns?: string[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
}

export const ReturnedAgGridTable = React.memo(function ReturnedAgGridTable({
  returned,
  isFetching = false, // 🎯 Añadir isFetching para control de opacidad
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
}: ReturnedAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // 🎯 Usar el hook para manejar las columnas (ahora con cellRenderers incluidos)
  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
  });

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => { }, []);

  // 🎯 Las columnas ya vienen con sus cellRenderers desde el hook
  // Ya no necesitamos aplicar renderers aquí, todo está en useAgGridColumns.tsx
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  return (
    <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Componente base AgGridTable */}
      <AgGridTable
        ref={agGridRef}
        data={returned}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 150px)"
        enableSelection={false} // 🎯 Returned NO tiene selección múltiple
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}} // Pasamos un objeto vacío para evitar conflictos
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
});

